import * as math from "mathjs";
import * as _ from "lodash";
import * as Rx from "rxjs";
import * as Util from "Util";
import * as mathjs from "mathjs";
import base32 = require("base32-crockford-browser");
import { Matrix } from "Math/Matrix";
import { AnyReal } from "Sauce/Validation/Basic";

export type Edge<E> = {from: string , to: string, val: E};
export class AdjacencyMatrixGraph<N, E> {
    protected matrix: Matrix<E>;
    protected idxes: { [ id :string ]: number } = {};
    protected nodeIds: { [ idx: number ]: string } = {};
    protected data: { [ id: string ]: N } = {};
    protected nextIdCounter = 0;
    graphChanged = new Rx.BehaviorSubject(this);
    constructor(filler: (i: number, j: number) => E, pretty?: (e: E) => string) {
        this.matrix = Matrix.make<E>(0, 0, filler, pretty ? pretty : e => JSON.stringify(e));
    }

    

    getEdge(fromId: string, toId: string) {
        let colIdx = this.idxes[fromId];
        let rowIdx = this.idxes[toId];

        return this.matrix.getEntry(rowIdx, colIdx);
    }
    getNodeIds() {
        return Object.keys(this.nodeIds).map(k => (this.nodeIds as any)[k]);
    }
    /** @returns an array of all nodes on the graph in index order */
    /** @returns a row-major array of arrays representing all edges, including null entries */
    getMatrixCopy(): Matrix<E> {
        return this.matrix.clone();
    }

    rawMatrixEntries(): (E | null)[][] {
        return this.matrix.raw;
    }

    /** returns a list of all non-null edges. Useful for sparse matrices */
    getNonNullEdges(): Edge<E>[] {
        let edges: Edge<E>[] = [];
        for (let i = 0; i < this.matrix.rows; i++) {
            for (let j = 0; j < this.matrix.cols; j++) {
                let entry = this.matrix.getEntry(i, j);
                if (entry != null) {
                    let [i_id, j_id] = [this.nodeIds[i], this.nodeIds[j]];
                    let from = this.data[i_id];
                    let to = this.data[j_id];
                    edges.push({ from: j_id, to: i_id, val: entry })
                }
            }
        }
        return edges;
    }

   nextIdNumber()  {
        return this.nextIdCounter;
    }

    addNode(id: string, payload?: N, fill?: (i: number, j: number) => E) {
        let idx = this.order();
        this.nextIdCounter++;

        this.idxes[id] = idx;
        this.nodeIds[idx] = id;

        if (payload != undefined) {
            this.data[id] = payload;
        }

        this.matrix.grow(1, 1, fill)
        this.graphChanged.next(this);
        return idx;
    }

    removeNode(id: string | string[]) {
        let _id = typeof id === "string" ? [id] : id;
        for (let id of _id) {
            let idx = this.idxes[id];
            if (idx != undefined) {
                delete this.idxes[id];
                delete this.nodeIds[idx];
                delete this.data[id];
                
                this.matrix.removeRows(idx);
                // delete the row at idx, and shift the idx of all
                // rows above it down one.
                for (let i = idx; i < this.matrix.rows; i++) {
                    this.nodeIds[i] = this.nodeIds[i+1];
                }
                delete this.nodeIds[this.matrix.rows];

                for (let i = idx; i < this.matrix.rows; i++) {
                    this.idxes[this.nodeIds[i]] -= 1;
                }
                delete this.idxes[this.nodeIds[this.matrix.rows]];

                this.matrix.removeColumns(idx);
            }
        }
        this.graphChanged.next(this);
    }
    serialize(): string {
        return base32.encode(JSON.stringify({
            idxes: this.idxes,
            nodeIds: this.nodeIds,
            data: this.data,
            matrix: this.matrix.serialize(),
            nextIdCounter: this.nextIdCounter
        }));

    }
    static deserialize<N, E>(serialization: string): AdjacencyMatrixGraph<N,E> {
        let data = JSON.parse(base32.decode(serialization));
        let graph = new AdjacencyMatrixGraph<N, E>(() => null);
        graph.idxes = data.idxes;
        graph.nodeIds = data.nodeIds;
        graph.data = data.data;
        graph.matrix = Matrix.deserialize<E>(data.matrix);
        graph.nextIdCounter = data.nextIdCounter;

        return graph;
    }
    pretty(formatter: (e: E) => string): string {
        return this.matrix.pretty(formatter)
    }

    private _setEdge(a: string, b: string, e: E) {
        this.matrix.setEntry(this.index(b), this.index(a), e);
    }

    setEdgeByIndex(i: number, j: number, val: E) {
        this.matrix.setEntry(i, j, val);
        this.graphChanged.next(this);
    }
    setEdge(a: string | string[], b: string | string[], e: E) {
        if (a instanceof Array) {
            if (b instanceof Array) {
                for (let _a of a) {
                    for (let _b of b) {
                        this._setEdge(_a, _b, e);
                    }
                }
            }
            else {
                for (let _a of a) {
                    this._setEdge(_a, b, e);
                }
            }
        }
        else {
            if (b instanceof Array) {
                for (let _b of b) {
                    this._setEdge(a, _b, e);
                }
            }
            else {
                this._setEdge(a, b, e);
            }
        }
        this.graphChanged.next(this);
    }
    getNodeById(id: string): N {
        return this.data[id];
    }
    getNode(idx: number) {
        return this.data[this.nodeIds[idx]];
    }
    setNode(id: string, data: Partial<N>): void {
        this.data[id] = Util.merge(data, this.data[id]);
        this.graphChanged.next(this);
    }
    private id(idx: number): string {
        let id = this.nodeIds[idx];
        if (id === undefined) {
            throw RangeError("No id for this index");
        }
        return this.nodeIds[idx];
    }
    private index(id: string): number {
        let idx = this.idxes[id];
        if (idx === undefined) {
            throw "Unknown id (" + id + ")";
        }
        return idx;
    }
    /** @returns The number of nodes in this graph */
    order(): number {
        return this.matrix.rows;
    }
    /** @returns the number of edges in this graph */
    degree(): number {
        throw "NYI";
    } 
}

export class EdgeWeightedAdjacencyMatrixGraph<T, E extends { weight: number | (() => number) }> extends AdjacencyMatrixGraph<T, E> {

    static deserialize<N, E extends { weight: number | (() => number) }>(serialization: string): EdgeWeightedAdjacencyMatrixGraph<N,E> {
        let data = JSON.parse(base32.decode(serialization));
        let graph = new EdgeWeightedAdjacencyMatrixGraph<N, E>(() => null);
        graph.idxes = data.idxes;
        graph.nodeIds = data.nodeIds;
        graph.data = data.data;
        graph.matrix = data.matrix;
        graph.nextIdCounter = data.nextIdCounter;

        return graph;
    }
}


// WEBPACK FOOTER //
// ./src/code/Math/Graph.ts